var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Group, Image, Line, Rect } from 'react-konva';
import { cacheImageAsync, cleanupCache, clearCacheAndResetFlags, createCacheParams, ensureCacheForFilters, getCachedImage, MAX_CANVAS_SIZE, shouldCacheImage, shouldRecache, } from '@/utils/board/imageCacheUtils';
import { prepareKonvaFilters } from '@/utils/board/imageFilters';
import { mapStrokeWidth } from '@/utils/board/strokeUtils';
// ==================== УТИЛИТЫ ====================
/**
 * Получает Konva узел из React ref
 */
const getKonvaNode = (imageRef) => {
    var _a, _b;
    if (!imageRef.current)
        return null;
    const node = ((_b = (_a = imageRef.current) === null || _a === void 0 ? void 0 : _a.getNode) === null || _b === void 0 ? void 0 : _b.call(_a)) || imageRef.current;
    return (node && typeof node.cache === 'function' ? node : null);
};
/**
 * Масштабирует размеры для предотвращения ошибок Canvas
 */
const scaleDimensionsToFitCanvas = (width, height) => {
    const maxDimension = Math.max(width, height);
    if (maxDimension <= MAX_CANVAS_SIZE) {
        return [width, height];
    }
    const scale = MAX_CANVAS_SIZE / maxDimension;
    return [width * scale, height * scale];
};
// ==================== ОБЩИЕ СВОЙСТВА ====================
const commonImageProps = {
    shadowForStrokeEnabled: false,
    perfectDrawEnabled: false,
};
// ==================== КОМПОНЕНТЫ ====================
/**
 * React компонент для изображения с фильтрами Konva.
 *
 * Особенности:
 * - Требует кэширования для работы фильтров (требование Konva)
 * - Использует глобальный реестр кэшированных изображений для предотвращения дублирования
 * - Автоматически очищает кэш при изменении параметров (zoom, размеры, crop)
 * - Скрывает изображение на 2 кадра при изменении crop для предотвращения артефактов
 */
const FilteredImage = ({ img, width, height, filters, brightness, contrast, opacity, name, zoom = 1, crop, cacheKey, isVisible = true, isVisibleForCache = true, isPanning = false, isZooming = false, }) => {
    const imageRef = useRef(null);
    const isCachedRef = useRef(false); // Флаг, указывающий, что изображение закэшировано
    const cacheFailedRef = useRef(false); // Флаг, указывающий, что кэширование не удалось
    const cacheParamsRef = useRef(null); // Параметры кэширования для отмены регистрации
    const [hideOnCropFrame, setHideOnCropFrame] = useState(false);
    // Кэширование для изображений с фильтрами (требование Konva)
    useEffect(() => {
        if (!img || !filters.length)
            return;
        const konvaNode = getKonvaNode(imageRef);
        if (!konvaNode)
            return;
        // Для фильтров кешируем только элементы в расширенной зоне кэширования (для экономии памяти)
        // Но если элемент не виден для кэширования, все равно нужно кешировать для фильтров (они требуют кеш)
        // Используем минимальный pixelRatio для невидимых элементов с фильтрами
        if (!isVisibleForCache) {
            // Для элементов вне расширенной зоны кэширования с фильтрами не кешируем (экономия памяти)
            // Фильтры будут применены при появлении элемента в зоне кэширования
            if (isCachedRef.current) {
                clearCacheAndResetFlags(konvaNode, cacheParamsRef, isCachedRef, cacheFailedRef);
            }
            return;
        }
        // Проверяем, нужно ли пересоздать кэш (изменились параметры)
        const needsRecache = shouldRecache(isCachedRef.current, cacheParamsRef.current, img, zoom, width, height);
        // Очищаем кэш при изменении параметров, но НЕ во время активного pan/zoom
        // Это предотвращает фризы во время панорамирования/зума
        if (isCachedRef.current && needsRecache && !isPanning && !isZooming) {
            clearCacheAndResetFlags(konvaNode, cacheParamsRef, isCachedRef, cacheFailedRef);
        }
        // Для фильтров Konva ОБЯЗАТЕЛЬНО нужен кэш на каждом узле
        // Кэшируем синхронно, чтобы кэш был готов до применения фильтров
        // НО только если не идет активный pan/zoom (для предотвращения фризов)
        // Если кэш уже есть и идет pan/zoom, не пересоздаем его
        if (!isPanning && !isZooming) {
            ensureCacheForFilters(konvaNode, img, zoom, width, height, cacheParamsRef, isCachedRef, cacheFailedRef);
        }
        else if (!isCachedRef.current) {
            // Если кэша нет и идет pan/zoom, все равно создаем его (фильтры требуют кэш)
            // Но только если кэша еще нет
            ensureCacheForFilters(konvaNode, img, zoom, width, height, cacheParamsRef, isCachedRef, cacheFailedRef);
        }
        // Cleanup: очищаем кэш и отменяем запланированные операции при размонтировании
        return () => {
            const node = getKonvaNode(imageRef);
            cleanupCache(node, cacheParamsRef);
            isCachedRef.current = false;
            cacheFailedRef.current = false;
        };
    }, [
        img,
        filters.length,
        brightness,
        contrast,
        width,
        height,
        zoom,
        crop,
        cacheKey,
        isVisibleForCache,
        isPanning,
        isZooming,
    ]);
    // Обновляем кэш для фильтров после завершения pan/zoom
    useEffect(() => {
        // Обновляем кэш только после завершения pan/zoom (когда оба флага false)
        if (isPanning || isZooming || !img || !filters.length) {
            return;
        }
        const konvaNode = getKonvaNode(imageRef);
        if (!konvaNode || !isVisibleForCache)
            return;
        // Проверяем, нужно ли обновить кэш после завершения pan/zoom
        const needsRecache = shouldRecache(isCachedRef.current, cacheParamsRef.current, img, zoom, width, height);
        if (needsRecache) {
            ensureCacheForFilters(konvaNode, img, zoom, width, height, cacheParamsRef, isCachedRef, cacheFailedRef);
        }
    }, [isPanning, isZooming, img, filters.length, zoom, width, height, isVisibleForCache]);
    // Скрываем изображение на два кадра только при смене crop и синхронно чистим кэш,
    // чтобы не показать старый кадр (на resize/move НЕ скрываем)
    useEffect(() => {
        var _a;
        if (!img || !crop)
            return;
        setHideOnCropFrame(true);
        // Синхронно чистим кэш текущего узла изображения
        const konvaNode = getKonvaNode(imageRef);
        if (konvaNode) {
            // Отменяем таймер и очищаем кэш с отменой регистрации
            clearCacheAndResetFlags(konvaNode, cacheParamsRef, isCachedRef, cacheFailedRef);
            // Обновляем слой для немедленного отображения изменений
            (_a = konvaNode.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
        }
        const id1 = requestAnimationFrame(() => {
            const id2 = requestAnimationFrame(() => {
                setHideOnCropFrame(false);
                // После показа — пересоздаем кэш для фильтров
                const node = getKonvaNode(imageRef);
                if (node) {
                    const cached = ensureCacheForFilters(node, img, zoom, width, height, cacheParamsRef, isCachedRef, cacheFailedRef);
                    if (cached) {
                        const layer = node.getLayer();
                        if (layer) {
                            layer.drawHit();
                        }
                    }
                    // Обновляем слой и hit-слой
                    const layer = node.getLayer();
                    layer === null || layer === void 0 ? void 0 : layer.batchDraw();
                    layer === null || layer === void 0 ? void 0 : layer.drawHit();
                }
            });
            return () => cancelAnimationFrame(id2);
        });
        return () => cancelAnimationFrame(id1);
    }, [img, crop, crop === null || crop === void 0 ? void 0 : crop.x, crop === null || crop === void 0 ? void 0 : crop.y, crop === null || crop === void 0 ? void 0 : crop.width, crop === null || crop === void 0 ? void 0 : crop.height, zoom, width, height]);
    const [safeWidth, safeHeight] = scaleDimensionsToFitCanvas(width, height);
    if (safeWidth !== width || safeHeight !== height) {
        console.warn(`[FilteredImage] Scaling image from ${width}x${height} to ${safeWidth}x${safeHeight} to fit Canvas limit`);
    }
    // Вычисляем crop для Konva.Image
    // В Konva crop работает относительно исходного изображения (naturalWidth/naturalHeight)
    // Поэтому применяем crop напрямую без масштабирования
    const konvaCrop = useMemo(() => {
        if (!img || !crop)
            return undefined;
        // crop уже в координатах исходного изображения, применяем напрямую
        return {
            x: crop.x,
            y: crop.y,
            width: crop.width,
            height: crop.height,
        };
    }, [img, crop]);
    return (_jsx(Image, Object.assign({ ref: imageRef, image: img, width: safeWidth, height: safeHeight, filters: filters.length > 0 ? filters : undefined }, (brightness !== undefined && { brightness }), (contrast !== undefined && { contrast }), (opacity !== undefined && opacity !== 1 && { opacity }), (konvaCrop && { crop: konvaCrop }), commonImageProps, { imageSmoothingEnabled: true, perfectDrawEnabled: false, shadowForStrokeEnabled: false, hitGraphEnabled: false, listening: false, visible: !hideOnCropFrame, name: name })));
};
/**
 * React компонент для обычных изображений без фильтров с кэшированием.
 *
 * Особенности:
 * - Оптимизирован для больших изображений и панорамирования
 * - Использует глобальный реестр кэшированных изображений для предотвращения дублирования
 * - Кэширует изображения асинхронно в фоне (requestIdleCallback)
 * - Автоматически очищает кэш при изменении параметров (zoom, размеры, crop)
 * - Скрывает изображение на 2 кадра при изменении crop для предотвращения артефактов
 * - Поддерживает отключение кэширования через проп disableCache
 */
const CachedImage = ({ img, width, height, opacity, name, zoom = 1, crop, disableCache, isVisible = true, isVisibleForCache = true, isPanning = false, isZooming = false, }) => {
    const imageRef = useRef(null);
    const isCachedRef = useRef(false);
    const cacheParamsRef = useRef(null);
    const [hideOnCropFrame, setHideOnCropFrame] = useState(false);
    // Кэшируем изображение асинхронно после первого рендера
    useEffect(() => {
        if (!img || isCachedRef.current)
            return;
        if (disableCache)
            return;
        const konvaNode = getKonvaNode(imageRef);
        if (!konvaNode)
            return;
        // Используем новую логику кеширования с проверкой видимости для кэширования
        if (shouldCacheImage(img, zoom, isVisibleForCache, width, height)) {
            // Сохраняем параметры кэширования для последующей отмены регистрации
            cacheParamsRef.current = createCacheParams(img, zoom, width, height);
            // Используем высокий приоритет для видимых изображений, низкий для невидимых
            const priority = isVisibleForCache ? 'high' : 'low';
            cacheImageAsync(konvaNode, img, zoom, isVisibleForCache, false, width, height, false, priority);
            isCachedRef.current = true;
        }
        // Cleanup: очищаем кэш и отменяем запланированные операции при размонтировании
        return () => {
            const node = getKonvaNode(imageRef);
            cleanupCache(node, cacheParamsRef);
            isCachedRef.current = false;
        };
    }, [img, zoom, width, height, disableCache, isVisibleForCache]);
    // Дебаунсинг для обновления кеша после pan/zoom
    const recacheTimeoutRef = useRef(null);
    // Обновляем кэш при изменении размера, зума, видимости или crop
    // НО НЕ во время активного pan/zoom - это предотвращает фризы
    useEffect(() => {
        // Не обновляем кэш во время активного pan/zoom
        if (isPanning || isZooming) {
            // Отменяем запланированное обновление кеша
            if (recacheTimeoutRef.current) {
                clearTimeout(recacheTimeoutRef.current);
                recacheTimeoutRef.current = null;
            }
            return;
        }
        // Отменяем предыдущее запланированное обновление
        if (recacheTimeoutRef.current) {
            clearTimeout(recacheTimeoutRef.current);
        }
        // Дебаунсим обновление кеша после завершения pan/zoom (500мс задержка)
        recacheTimeoutRef.current = setTimeout(() => {
            const konvaNode = getKonvaNode(imageRef);
            if (!konvaNode || !img)
                return;
            // Если кэш был создан, проверяем нужно ли его обновить
            if (isCachedRef.current) {
                const needsRecache = shouldRecache(isCachedRef.current, cacheParamsRef.current, img, zoom, width, height);
                if (needsRecache) {
                    clearCacheAndResetFlags(konvaNode, cacheParamsRef, isCachedRef);
                }
                else {
                    // Кеш актуален, не пересоздаем
                    return;
                }
            }
            if (disableCache)
                return;
            // Пересоздаем кэш если нужно (используем новую логику с проверкой видимости для кэширования)
            if (shouldCacheImage(img, zoom, isVisibleForCache, width, height)) {
                // Сохраняем параметры кэширования для последующей отмены регистрации
                cacheParamsRef.current = createCacheParams(img, zoom, width, height);
                // Используем высокий приоритет для видимых изображений, низкий для невидимых
                const priority = isVisibleForCache ? 'high' : 'low';
                cacheImageAsync(konvaNode, img, zoom, isVisibleForCache, false, width, height, false, priority);
                isCachedRef.current = true;
            }
            else {
                isCachedRef.current = false;
            }
        }, 500); // 500мс задержка для дебаунсинга
        return () => {
            if (recacheTimeoutRef.current) {
                clearTimeout(recacheTimeoutRef.current);
                recacheTimeoutRef.current = null;
            }
        };
    }, [width, height, zoom, img, crop, disableCache, isVisibleForCache, isPanning, isZooming]);
    // Скрываем изображение на два кадра только при смене crop и синхронно чистим кэш,
    // чтобы не показать старый кадр (на resize/move НЕ скрываем)
    useEffect(() => {
        var _a;
        if (!img || !crop)
            return;
        setHideOnCropFrame(true);
        // Синхронно чистим кэш текущего узла изображения
        const konvaNode = getKonvaNode(imageRef);
        if (konvaNode) {
            // Отменяем таймер и очищаем кэш с отменой регистрации
            cleanupCache(konvaNode, cacheParamsRef);
            // Обновляем слой для немедленного отображения изменений
            (_a = konvaNode.getLayer()) === null || _a === void 0 ? void 0 : _a.batchDraw();
        }
        const id1 = requestAnimationFrame(() => {
            const id2 = requestAnimationFrame(() => {
                setHideOnCropFrame(false);
                // После показа — обновим слой и hit-слой
                const node = getKonvaNode(imageRef);
                const layer = node === null || node === void 0 ? void 0 : node.getLayer();
                layer === null || layer === void 0 ? void 0 : layer.batchDraw();
                layer === null || layer === void 0 ? void 0 : layer.drawHit();
            });
            return () => cancelAnimationFrame(id2);
        });
        return () => cancelAnimationFrame(id1);
    }, [img, crop, crop === null || crop === void 0 ? void 0 : crop.x, crop === null || crop === void 0 ? void 0 : crop.y, crop === null || crop === void 0 ? void 0 : crop.width, crop === null || crop === void 0 ? void 0 : crop.height]);
    const [safeWidth, safeHeight] = scaleDimensionsToFitCanvas(width, height);
    if (safeWidth !== width || safeHeight !== height) {
        console.warn(`[CachedImage] Scaling image from ${width}x${height} to ${safeWidth}x${safeHeight} to fit Canvas limit`);
    }
    // Вычисляем crop для Konva.Image
    // В Konva crop работает относительно исходного изображения (naturalWidth/naturalHeight)
    // Поэтому применяем crop напрямую без масштабирования
    const konvaCrop = useMemo(() => {
        if (!img || !crop)
            return undefined;
        // crop уже в координатах исходного изображения, применяем напрямую
        return {
            x: crop.x,
            y: crop.y,
            width: crop.width,
            height: crop.height,
        };
    }, [img, crop]);
    return (_jsx(Image, Object.assign({ ref: imageRef, image: img, width: safeWidth, height: safeHeight }, (opacity !== undefined && { opacity }), (konvaCrop && { crop: konvaCrop }), commonImageProps, { imageSmoothingEnabled: true, visible: !hideOnCropFrame, name: name, listening: false, hitGraphEnabled: false, perfectDrawEnabled: false, shadowForStrokeEnabled: false })));
};
/**
 * Компонент для загрузки и отображения изображения на доске.
 * Обрабатывает асинхронную загрузку изображений и не отрисовывает сломанные изображения.
 */
const ImageGroupComponent = ({ el, showElementSelectionBorder, layerLocked, common, clipFunc, zoom = 1, onDoubleClick, disableCache, isVisible = true, isVisibleForCache = true, isPanning = false, isZooming = false, isPresentationMode = false, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const [img, setImg] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    // Загрузка изображения
    useEffect(() => {
        let cancelled = false;
        const loadImage = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            setIsLoading(true);
            try {
                const loadedImg = yield getCachedImage((_a = el.asset) === null || _a === void 0 ? void 0 : _a.url);
                if (cancelled)
                    return;
                if (loadedImg) {
                    if (loadedImg.naturalWidth > MAX_CANVAS_SIZE ||
                        loadedImg.naturalHeight > MAX_CANVAS_SIZE) {
                        console.warn(`[ImageGroup] Large image detected: ${loadedImg.naturalWidth}x${loadedImg.naturalHeight}px (element ${el.id}). Canvas limit: ${MAX_CANVAS_SIZE}px. Will render without cache.`);
                    }
                    setImg(loadedImg);
                }
                else {
                    console.warn(`[ImageGroup] Failed to load image for element ${el.id}`);
                }
                setIsLoading(false);
            }
            catch (error) {
                if (!cancelled) {
                    console.warn(`[ImageGroup] Error loading image for element ${el.id}:`, error);
                    setImg(undefined);
                    setIsLoading(false);
                }
            }
        });
        loadImage();
        return () => {
            cancelled = true;
        };
    }, [(_a = el.asset) === null || _a === void 0 ? void 0 : _a.url, el.id]);
    // Извлечение props элемента
    const w = ((_b = el.props) === null || _b === void 0 ? void 0 : _b.w) || 64;
    const h = ((_c = el.props) === null || _c === void 0 ? void 0 : _c.h) || 64;
    const localGrid = useMemo(() => { var _a; return ((_a = el.props) === null || _a === void 0 ? void 0 : _a.localGrid) || { enabled: false, size: 40, color: '#ffffffff' }; }, [(_d = el.props) === null || _d === void 0 ? void 0 : _d.localGrid]);
    const { konvaFilters, brightness, contrast, opacity } = useMemo(() => { var _a; return prepareKonvaFilters((_a = el.props) === null || _a === void 0 ? void 0 : _a.filters); }, [(_e = el.props) === null || _e === void 0 ? void 0 : _e.filters]);
    const crop = ((_f = el.props) === null || _f === void 0 ? void 0 : _f.crop) || null;
    // Версия элемента для инвалидации кэша при обновлении с сервера
    const elementVersion = (el === null || el === void 0 ? void 0 : el.version) ||
        (el === null || el === void 0 ? void 0 : el.updatedAt) ||
        (el === null || el === void 0 ? void 0 : el.updated_at) ||
        ((_g = el.props) === null || _g === void 0 ? void 0 : _g.version) ||
        ((_h = el.props) === null || _h === void 0 ? void 0 : _h.updatedAt) ||
        ((_j = el.props) === null || _j === void 0 ? void 0 : _j.rev) ||
        ((_k = el.props) === null || _k === void 0 ? void 0 : _k.ts) ||
        undefined;
    const cacheKey = `${el.id}-${elementVersion !== null && elementVersion !== void 0 ? elementVersion : 'nover'}`;
    // Диагностика размеров элемента
    useEffect(() => {
        if (w > MAX_CANVAS_SIZE || h > MAX_CANVAS_SIZE) {
            console.warn(`[ImageGroup] Element ${el.id} has very large dimensions: ${w}x${h}px. Canvas limit: ${MAX_CANVAS_SIZE}px. Image may not render correctly.`);
        }
    }, [w, h, el.id]);
    // Генерация линий сетки
    const gridLines = useMemo(() => {
        if (!localGrid.enabled)
            return null;
        let localGridStrokeWidth;
        let localGridStrokeDash;
        if (localGrid.size >= 100) {
            localGridStrokeWidth = 1;
            localGridStrokeDash = [1, 1];
        }
        else if (localGrid.size >= 50) {
            localGridStrokeWidth = 0.8;
            localGridStrokeDash = [1, 1];
        }
        else if (localGrid.size >= 25) {
            localGridStrokeWidth = 0.5;
            localGridStrokeDash = [0.8, 0.8];
        }
        else if (localGrid.size >= 10) {
            localGridStrokeWidth = 0.3;
            localGridStrokeDash = [0.5, 0.5];
        }
        else {
            localGridStrokeWidth = 0.1;
            localGridStrokeDash = [0.2, 0.2];
        }
        return [
            ...Array.from({ length: Math.floor(w / localGrid.size) + 1 }, (_, i) => (_jsx(Line, { points: [i * localGrid.size, 0, i * localGrid.size, h], stroke: localGrid.color, strokeWidth: localGridStrokeWidth, dash: localGridStrokeDash, listening: false, hitGraphEnabled: false, perfectDrawEnabled: false, shadowForStrokeEnabled: false, scaleEnabled: true, opacity: 0.6 }, `v-${i}`))),
            ...Array.from({ length: Math.floor(h / localGrid.size) + 1 }, (_, i) => (_jsx(Line, { points: [0, i * localGrid.size, w, i * localGrid.size], stroke: localGrid.color, strokeWidth: localGridStrokeWidth, dash: localGridStrokeDash, listening: false, hitGraphEnabled: false, perfectDrawEnabled: false, shadowForStrokeEnabled: false, scaleEnabled: true, opacity: 0.6 }, `h-${i}`))),
        ];
    }, [localGrid.enabled, localGrid.size, localGrid.color, w, h]);
    // Настройки обводки
    const strokeConfig = useMemo(() => {
        var _a, _b, _c;
        const strokeValue = (_a = el.props) === null || _a === void 0 ? void 0 : _a.stroke;
        const logicalStrokeWidth = typeof ((_b = el.props) === null || _b === void 0 ? void 0 : _b.strokeWidth) === 'number' && !isNaN(el.props.strokeWidth)
            ? el.props.strokeWidth
            : 5; // Значение по умолчанию 5 (маппится в 1)
        const stroke = typeof strokeValue === 'string' && strokeValue ? strokeValue : undefined;
        let strokeEnabled = false;
        if (isPresentationMode && el.isPresentationFrame) {
            strokeEnabled = false;
        }
        else {
            strokeEnabled =
                typeof ((_c = el.props) === null || _c === void 0 ? void 0 : _c.strokeEnabled) === 'boolean'
                    ? el.props.strokeEnabled
                    : stroke !== undefined && stroke !== null;
        }
        // Применяем маппинг для физического размера обводки
        const strokeWidth = mapStrokeWidth(logicalStrokeWidth);
        return {
            stroke,
            strokeWidth,
            strokeEnabled,
            commonRectProps: Object.assign({ stroke,
                strokeWidth, strokeScaleEnabled: true, listening: false }, commonImageProps),
        };
    }, [
        (_l = el.props) === null || _l === void 0 ? void 0 : _l.stroke,
        (_m = el.props) === null || _m === void 0 ? void 0 : _m.strokeWidth,
        (_o = el.props) === null || _o === void 0 ? void 0 : _o.strokeEnabled,
        isPresentationMode,
        el.isPresentationFrame,
    ]);
    const clip = (_p = el.props) === null || _p === void 0 ? void 0 : _p.clip;
    // Вычисляем пропорциональное смещение тени
    const tokenShadowOffset = useMemo(() => {
        const avgSize = (w + h) / 2;
        return Math.max(0.8, avgSize * 0.02);
    }, [w, h]);
    // Вычисляем параметры круга для тени
    const tokenShadowParams = useMemo(() => {
        var _a;
        if (!clip || clip.type !== 'circle')
            return null;
        let r = (_a = clip.r) !== null && _a !== void 0 ? _a : Math.min(w, h) / 2;
        if (strokeConfig.strokeEnabled) {
            r = r + strokeConfig.strokeWidth / 2;
        }
        return {
            x: w / 2 + tokenShadowOffset,
            y: h / 2 + tokenShadowOffset,
            radius: r,
        };
    }, [clip, w, h, tokenShadowOffset, strokeConfig.strokeEnabled, strokeConfig.strokeWidth]);
    return (_jsxs(Group, Object.assign({}, common, { visible: isVisible, onDblClick: onDoubleClick
            ? (e) => {
                console.log('[ImageGroup] Double click on image', el.id);
                onDoubleClick(e, el.id);
            }
            : undefined, children: [tokenShadowParams && (_jsx(Circle, { x: tokenShadowParams.x, y: tokenShadowParams.y, radius: tokenShadowParams.radius, fill: "#000000", opacity: 0.7, listening: false, hitGraphEnabled: false, perfectDrawEnabled: false, shadowForStrokeEnabled: false })), _jsxs(Group, { clipFunc: clipFunc, children: [isLoading && (_jsx(Rect, Object.assign({ width: w, height: h, fill: "#555555", opacity: 0.4, listening: false, hitGraphEnabled: false }, commonImageProps))), !isLoading && img && (_jsx(_Fragment, { children: konvaFilters.length > 0 ? (_jsx(FilteredImage, { img: img, width: w, height: h, filters: konvaFilters, brightness: brightness, contrast: contrast, opacity: opacity, name: el.id.toString(), zoom: zoom, crop: crop, cacheKey: cacheKey, isVisible: isVisible, isVisibleForCache: isVisibleForCache, isPanning: isPanning, isZooming: isZooming })) : (_jsx(CachedImage, { img: img, width: w, height: h, opacity: opacity, name: el.id.toString(), zoom: zoom, crop: crop, cacheKey: cacheKey, disableCache: disableCache, isVisible: isVisible, isVisibleForCache: isVisibleForCache, isPanning: isPanning, isZooming: isZooming })) })), !isLoading && !img && (_jsx(Rect, Object.assign({ width: w, height: h, fill: "#ff0000", opacity: 0.3, listening: false, hitGraphEnabled: false }, commonImageProps))), gridLines] }), _jsx(Rect, Object.assign({ width: w, height: h, fill: "rgba(0,0,0,0.0001)", opacity: 0.0001, listening: true }, commonImageProps)), strokeConfig.strokeEnabled && clip && (_jsx(_Fragment, { children: clip.type === 'circle' && (_jsx(Line, Object.assign({ sceneFunc: (ctx, shape) => {
                        var _a;
                        const r = (_a = clip.r) !== null && _a !== void 0 ? _a : Math.min(w, h) / 2;
                        ctx.beginPath();
                        ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
                        ctx.closePath();
                        ctx.fillStrokeShape(shape);
                    } }, strokeConfig.commonRectProps))) })), strokeConfig.strokeEnabled && !clip && (_jsx(Rect, Object.assign({ width: w, height: h }, strokeConfig.commonRectProps))), showElementSelectionBorder && (_jsx(Rect, Object.assign({ width: ((_q = el.props) === null || _q === void 0 ? void 0 : _q.w) || 64, height: ((_r = el.props) === null || _r === void 0 ? void 0 : _r.h) || 64, stroke: el.locked || layerLocked ? 'rgba(150, 164, 167, 1)' : '#5eafffff', strokeWidth: 2, strokeScaleEnabled: false, listening: false, hitGraphEnabled: false }, commonImageProps)))] }), el.id));
};
/**
 * Функция-рендерер для элемента изображения.
 * Возвращает React компонент для правильной работы с асинхронной загрузкой изображений.
 */
export function imageGroup(params) {
    return _jsx(ImageGroupComponent, Object.assign({}, params));
}
